// src/components/CustomFields/fields/MultiSelectField.tsx
// MultiSelectField component migrated from legacy

import React from 'react';
import Select from 'react-select';
import type { MultiValue, StylesConfig } from 'react-select';
import * as APIUtils from '../../Globals/Utils';
import $ from 'jquery';
import CustomFieldsService from '../../../api/CustomFieldsService';
import { RenderInBody } from '../../Globals';
import shortid from 'shortid';
import './MultiSelectField.css';
import { isSafari } from './DOMHelper';
import type { MultiSelectFieldProps, CustomFieldItem } from './types';

// react-select v5 styles matching legacy Corgee appearance
const getMultiSelectStyles = (isDark: boolean): StylesConfig<any, true> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '24px',
    borderRadius: 0,
    boxShadow: 'none',
    fontSize: '11px',
    backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
    borderColor: state.isFocused ? '#62accb' : isDark ? '#4a4c4c' : '#ccc',
    width: '125px',
    '&:hover': {
      borderColor: '#62accb',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '2px 6px',
    maxHeight: '120px',
    overflowX: 'hidden',
    overflowY: 'auto',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    height: '22px',
    color: isDark ? '#a9a093' : base.color,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '2px',
    paddingTop: '4px',
    verticalAlign: 'top',
    width: '20px',
    color: isDark ? '#6e6860' : base.color,
    '&:hover': {
      color: isDark ? '#a9a093' : '#666',
    },
  }),
  placeholder: base => ({
    ...base,
    lineHeight: '24px',
    fontSize: '11px',
    color: isDark ? '#6e6860' : '#aaa',
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: 'transparent',
    borderColor: isDark ? '#4a4c4c' : '#ddd',
    border: `1px solid ${isDark ? '#4a4c4c' : '#ddd'}`,
    position: 'relative',
  }),
  multiValueLabel: base => ({
    ...base,
    color: isDark ? '#a9a093' : '#666',
    paddingRight: '24px',
    borderRadius: '4px',
    fontSize: '11px',
  }),
  multiValueRemove: base => ({
    ...base,
    color: isDark ? '#6e6860' : '#666',
    border: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      color: isDark ? '#a9a093' : '#333',
    },
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: 0,
    width: '125px',
    zIndex: 9999,
    backgroundColor: isDark ? '#333535' : base.backgroundColor,
    border: isDark ? '1px solid #4a4c4c' : base.border,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '130px',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    lineHeight: '1.8',
    padding: '2px 6px',
    minHeight: 'auto',
    backgroundColor: state.isFocused
      ? isDark
        ? '#3a3c3c'
        : '#62accb'
      : isDark
        ? '#333535'
        : '#fff',
    color: state.isFocused ? (isDark ? '#e5e7eb' : '#fff') : isDark ? '#a9a093' : '#666666',
    border: state.isFocused ? '1px solid #eee' : '1px solid transparent',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: isDark ? '#3c6c80' : '#62accb',
    },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: '11px',
    color: isDark ? '#6e6860' : base.color,
    backgroundColor: isDark ? '#333535' : base.backgroundColor,
  }),
});

interface MultiSelectFieldState {
  canEdit: boolean;
  customField: CustomFieldItem;
  selectedValues: string[];
  selectedText: string;
  items: any[];
  editorEnabled: boolean;
  api: { url: string; token: string };
  listMenuStyle: { left: string; top: string; position: string; width: string };
  elementId: string;
  previousWidth: number;
  isProjectView: boolean;
  isFromInfoWindow: boolean;
  targetTop: number;
  targetTopFactor: number;
  isFromTabDetails: boolean;
  isConversationDetailsView: boolean;
  listPosition: { left: number; top: number };
}

class MultiSelectField extends React.Component<MultiSelectFieldProps, MultiSelectFieldState>
{
  private corgeeMultiSelectRef = React.createRef<HTMLDivElement>();
  private selectComboMultiRef = React.createRef<any>();

  constructor(props: MultiSelectFieldProps)
  {
    super(props);
    const cf = props.customField;

    const stringValueArr: string[] =
      cf.Value != null && cf.Value !== '' ? String(cf.Value).split(',').filter(Boolean) : [];

    this.state = {
      canEdit:
        (cf as any).SummatoryPeoplePhases === true
          ? false
          : props.canEdit && cf.ConversationIsEnable !== false,
      customField: cf,
      selectedValues: stringValueArr,
      selectedText: cf.SelectedValuesText || '',
      items: [],
      editorEnabled: false,
      api: {
        url: props.apiConfig.url,
        token: props.apiConfig.token,
      },
      listMenuStyle: {
        left: '0px',
        top: '0px',
        position: 'fixed',
        width: '150px',
      },
      elementId: shortid.generate(),
      previousWidth: 0,
      isProjectView: window.location.href.indexOf('ProjectDetails') > -1,
      isFromInfoWindow: false,
      targetTop: 0,
      targetTopFactor: 0,
      isFromTabDetails: false,
      isConversationDetailsView: window.location.href.indexOf('ConversationDetails') > -1,
      listPosition: {
        left: 0,
        top: 0,
      },
    };

    this.enableEditor = this.enableEditor.bind(this);
    this.save = this.save.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.getDataBy = this.getDataBy.bind(this);
    this.getSelectedGroupsByIds = this.getSelectedGroupsByIds.bind(this);
    this.onSelectValuesChange = this.onSelectValuesChange.bind(this);
    this.onUpdateCustomField = this.onUpdateCustomField.bind(this);
    this.onApplyStyle = this.onApplyStyle.bind(this);
    this.onCloseSelect = this.onCloseSelect.bind(this);
  }

  componentDidMount()
  {
    if (this.corgeeMultiSelectRef.current)
    {
      const containerElement = $(this.corgeeMultiSelectRef.current);
      if (containerElement.closest('.details-tab-directive').length > 0)
      {
        this.setState({ isFromInfoWindow: true });
      }
      if (containerElement.closest('#canvasDetails').length > 0)
      {
        this.setState({ isFromTabDetails: true });
      }
    }
  }

  onApplyStyle()
  {
    if (!this.state.isFromInfoWindow && !this.state.isFromTabDetails)
    {
      const isProjectView = window.location.href.indexOf('ProjectDetails') > -1;
      this.setState({ isProjectView: isProjectView });

      const containerElement = $(this.corgeeMultiSelectRef.current!);
      containerElement
        .closest('.layer-custom-fields-container')
        .first()
        .on('scroll', this.onCloseSelect);
    }
  }

  onCloseSelect()
  {
    this.setState({ editorEnabled: false });
    $(document).off('.clickOutside');
  }

  componentDidUpdate(prevProps: MultiSelectFieldProps)
  {
    const prevValue = String(prevProps.customField.Value || '');
    const nextValue = String(this.props.customField.Value || '');

    if (
      prevValue !== nextValue ||
      prevProps.customField.CustomFieldId !== this.props.customField.CustomFieldId
    )
    {
      const stringValueArr: string[] = nextValue !== '' ? nextValue.split(',').filter(Boolean) : [];
      this.setState({
        customField: this.props.customField,
        selectedText: (this.props.customField as any).SelectedValuesText || '',
        selectedValues: stringValueArr,
      });
    }
  }

  onSelectValuesChange(itemsSelected: MultiValue<any>)
  {
    const { customField } = this.state;
    const valueField = (customField as any).ValueField;
    const textField = (customField as any).TextField;

    const selectedValuesCopy = Array.from(itemsSelected).map(item => String(item[valueField]));
    const selectedTextCopy = Array.from(itemsSelected).map(item => item[textField]);

    let selectedTextString = '';
    if (selectedValuesCopy.length === 0)
    {
      selectedTextString = 'Not Set';
    }
    else
    {
      selectedTextString = selectedTextCopy.join(', ');
    }
    this.setState({ selectedValues: selectedValuesCopy, selectedText: selectedTextString });
  }

  save()
  {
    const { customField, selectedValues, selectedText } = this.state;

    let lastValuesString: string | undefined = undefined;
    if (customField.Value !== undefined && customField.Value !== null && customField.Value !== '')
    {
      lastValuesString =
        String(customField.Value) !== selectedValues.join(',')
          ? String(customField.Value)
          : undefined;
    }

    const selectedValuesString = selectedValues.join(',');

    const copyCustomField = { ...this.state.customField };
    copyCustomField.Value = selectedValuesString;
    copyCustomField.PreviousValue = lastValuesString;
    (copyCustomField as any).SelectedValuesText = selectedText;

    const requestSave = {
      api: this.state.api,
      request: copyCustomField,
    };

    if (customField.CustomFieldValueId === 0)
    {
      CustomFieldsService.savePost(requestSave).then((data: any) =>
      {
        copyCustomField.CustomFieldValueId = data.CustomFieldValueId;
        this.setState({ customField: copyCustomField }, () =>
        {
          this.onUpdateCustomField();
        });
      });
    }
    else
    {
      CustomFieldsService.save(requestSave).then(() =>
      {
        this.setState({ customField: copyCustomField }, () =>
        {
          this.onUpdateCustomField();
        });
      });
    }
    this.setState({ editorEnabled: false });
    $(document).off('.clickOutside');
  }

  onBlur()
  {
    const { disableAutoSave } = this.props;

    const element = $(this.corgeeMultiSelectRef.current!);
    element.closest('.layer-details-container').css('overflow', '');
    $('body').css('overflow', '');

    const elemReset = document.getElementsByClassName('responsiveInfoDetail');
    if (elemReset.length !== 0)
    {
      (elemReset[0] as HTMLElement).style.width = `${this.state.previousWidth}px`;
      (elemReset[0] as HTMLElement).className =
        'corgee_com_custom-fields-field-A _DEL_field-A custom-field-id';
    }

    const infoWindowScrollContainer = element.closest('.details-tab-directive .card-body-area');
    if (infoWindowScrollContainer.length > 0)
    {
      infoWindowScrollContainer.css('overflow', '');
    }

    element.closest('.layer-details-container').off('scroll');
    element.closest('.card-body-area').off('scroll');
    element.closest('md-dialog-content').off('scroll');
    element.closest('.add-inline-task').off('scroll');
    element.closest('.corgee_com_card-body-info').off('scroll');

    const { customField, selectedValues, selectedText } = this.state;

    let continueSave = true;
    if ((customField.Value === null || customField.Value === '') && selectedValues.length === 0)
    {
      continueSave = false;
    }
    if (continueSave)
    {
      if (!disableAutoSave)
      {
        this.save();
      }
      else
      {
        let lastValuesString: string | undefined = undefined;
        if (customField.Value !== undefined && customField.Value !== null)
        {
          lastValuesString =
            String(customField.Value) !== selectedValues.join(',')
              ? String(customField.Value)
              : undefined;
        }

        const selectedValuesString = selectedValues.join(',');

        const copyCustomField = { ...this.state.customField };
        copyCustomField.Value = selectedValuesString;
        copyCustomField.PreviousValue = lastValuesString;
        (copyCustomField as any).SelectedValuesText = selectedText;

        this.setState({ customField: copyCustomField }, () =>
        {
          this.onUpdateCustomField();
        });
      }
    }

    this.onCloseSelect();
  }

  getDataBy(): Promise<any> | undefined
  {
    const {
      customField,
      isFromMultiselect,
      conversationIds,
      configValueId,
      projectId,
      securityGroupIds,
      groups,
    } = this.props;
    if (customField)
    {
      let groupIds = '';

      if (groups !== undefined)
      {
        groupIds = this.getSelectedGroupsByIds(groups);
      }

      let serviceUrl = (customField as any).ServiceUrl;
      if (serviceUrl.indexOf('?') === -1)
      {
        serviceUrl += '?';
      }
      else
      {
        serviceUrl += '&';
      }

      if (isFromMultiselect && (customField as any).ValueField === 'PersonID')
      {
        return APIUtils.callFetch(
          this.state.api,
          serviceUrl + 'conversationIds=' + conversationIds + '&layerId=' + customField.LayerId,
        );
      }
      else
      {
        return APIUtils.callFetch(
          this.state.api,
          serviceUrl +
            'configValueId=' +
            configValueId +
            '&layerId=' +
            customField.LayerId +
            (projectId != null ? '&projectId=' + projectId : '') +
            (securityGroupIds ? '&securitygroups=' + securityGroupIds : '') +
            (groupIds ? '&groups=' + groupIds : ''),
        );
      }
    }
    return undefined;
  }

  getSelectedGroupsByIds(selectedGroupsIds: { GroupId: number }[]): string
  {
    const groups: number[] = [];
    for (let i = 0; i < selectedGroupsIds.length; i++)
    {
      groups.push(selectedGroupsIds[i]!.GroupId);
    }
    return groups && groups.length > 0 ? groups.join(',') : '';
  }

  enableEditor(e: React.MouseEvent)
  {
    const { canEdit } = this.state;

    if (canEdit)
    {
      const screenWidth = $('body');
      const widthScreen = screenWidth[0]?.offsetWidth ?? 0;
      const windowX = e.clientX;
      const elemCustField = (e.target as HTMLElement).closest(
        'div.corgee_com_custom-fields-field-A',
      ) as HTMLElement;

      this.setState(
        {
          previousWidth: elemCustField?.offsetWidth || 0,
        },
        () =>
        {
          if (155 + windowX > widthScreen && elemCustField)
          {
            elemCustField.className += ' responsiveInfoDetail ';
            elemCustField.style.width = '135px';
          }
        },
      );

      setTimeout(() =>
      {
        this.getDataBy()?.then((response: any) =>
        {
          const data = Array.isArray(response) ? response : [];
          this.setState({ items: data });

          if (this.selectComboMultiRef.current)
          {
            this.selectComboMultiRef.current.focus();
          }
        });
      });

      if (!isSafari())
      {
        $('body').css('overflow', 'hidden');
      }

      const window$ = $('.corgee_com_custom-fields-combobox-multiselect');
      const element = $(this.corgeeMultiSelectRef.current!);
      if (window$.length > 0)
      {
        element.closest('.layer-details-container').css('overflow', 'hidden');
      }

      this.setState({ editorEnabled: !this.state.editorEnabled }, () =>
      {
        const windowModal = $('.corgee_com_custom-fields-combobox-multiselect');
        const element$ = $(this.corgeeMultiSelectRef.current!);
        const control = element$.find('#select-target-' + this.state.elementId);
        let top = 0;
        let left = 0;

        if (
          windowModal.offset() &&
          windowModal.length > 0 &&
          windowModal.find('[id*="select-render"]').length > 0
        )
        {
          left = control.offset()!.left - windowModal.offset()!.left;
          top = control.offset()!.top + 35 - windowModal.offset()!.top;
        }
        else
        {
          if (control.length > 0)
          {
            left = control.offset()!.left;
            top = control.offset()!.top - ($('html').scrollTop() || 0);

            if (isSafari())
            {
              top = top - ($('body').scrollTop() || 0);
            }
          }
        }

        this.setState({
          listPosition: {
            left: left,
            top: top,
          },
        });

        setTimeout(() =>
        {
          element$.closest('.layer-custom-fields-container').on('scroll', () =>
          {
            this.onBlur();
          });

          element$.closest('.corgee_com_card-body-info').on('scroll', () =>
          {
            this.onBlur();
          });
        }, 100);
      });

      setTimeout(() =>
      {
        $(document).on('click.clickOutside', e =>
        {
          const isInsideField = $(e.target).closest(this.corgeeMultiSelectRef.current!).length > 0;

          // Use coordinates instead of DOM traversal: react-select re-renders option nodes
          // on selection, so the original target element may already be detached when this
          // handler fires, causing closest() to fail even for clicks inside the dropdown.
          const dropdownEl = document.getElementById('select-render-' + this.state.elementId);
          let isInsideDropdown = false;
          if (dropdownEl)
          {
            const rect = dropdownEl.getBoundingClientRect();
            isInsideDropdown =
              (e as any).clientX >= rect.left &&
              (e as any).clientX <= rect.right &&
              (e as any).clientY >= rect.top &&
              (e as any).clientY <= rect.bottom;
          }

          if (!isInsideField && !isInsideDropdown)
          {
            this.onBlur();
          }
        });
      }, 100);
    }
  }

  onUpdateCustomField()
  {
    if (this.props.UpdateCustomFields)
    {
      this.props.UpdateCustomFields(this.state.customField);
    }
  }

  render()
  {
    const {
      customField,
      canEdit,
      items,
      editorEnabled,
      selectedValues,
      selectedText,
      isFromInfoWindow,
      isFromTabDetails,
      listPosition,
    } = this.state;
    const isDark = document.documentElement.classList.contains('dark');
    const multiSelectSelectStyles = getMultiSelectStyles(isDark);
    const styleRender: React.CSSProperties = {
      position: 'fixed',
      zIndex: 999999,
      top: listPosition.top,
      left: listPosition.left,
    };
    const containerSelectorId = 'select-target-' + this.state.elementId;

    if (isFromInfoWindow || isFromTabDetails)
    {
      (styleRender as any).fontSize = 15;
      styleRender.position = 'absolute';
    }

    return (
      <div
        ref={this.corgeeMultiSelectRef}
        className="corgee_com_custom-fields-field-A _DEL_field-A"
      >
        {customField.GroupMultiEditionType === 1 && (
          <i
            className="material-icons several-values enable-help-tooltip"
            title="Not all cards selected have this detail. The detail value will change only on applicable cards."
            data-title="Not all cards selected have this detail. The detail value will change only on applicable cards."
          >
            do_not_disturb_on
          </i>
        )}
        <span className="corgee_com_custom-fields-title _DEL_title">{customField.Name}</span>
        <div className="click-to-edit">
          {!editorEnabled ? (
            <div
              onClick={this.enableEditor}
              className={canEdit ? 'lbl-editable-input' : 'lbl-non-editable-input'}
            >
              {selectedText || 'Not Set'}
            </div>
          ) : null}
          {editorEnabled && (
            <div id={containerSelectorId} style={{ margin: 0, padding: 0 }}>
              <RenderInBody
                containerStyles={
                  "position:'fixed'; top:" +
                  listPosition.top +
                  'px; left:' +
                  listPosition.left +
                  'px; zIndex:999999'
                }
              >
                <div
                  id={'select-render-' + this.state.elementId}
                  className="corgee_com_custom-fields-combobox-multiselect _DEL_comboBoxRef _DEL_multiselect-hidden-control-container"
                  style={styleRender}
                >
                  <Select
                    ref={this.selectComboMultiRef}
                    autoFocus={true}
                    menuIsOpen={true}
                    options={items}
                    onChange={this.onSelectValuesChange}
                    onMenuOpen={this.onApplyStyle}
                    value={items.filter((item: any) =>
                      selectedValues.includes(String(item[(customField as any).ValueField])),
                    )}
                    isMulti={true}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    isClearable={false}
                    getOptionLabel={(opt: any) => opt[(customField as any).TextField]}
                    getOptionValue={(opt: any) => String(opt[(customField as any).ValueField])}
                    styles={multiSelectSelectStyles}
                  />
                </div>
              </RenderInBody>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default MultiSelectField;
